/**
 * Reusable star button for toggling per-user favorites on a venue, series,
 * or individual event. Pure UI signal — does NOT trigger notifications.
 *
 * Hidden for anonymous users (consistent with `SubscribeButton`).
 *
 * Accessibility:
 * - Renders a real <button> with `aria-pressed` to convey toggle state.
 * - Dynamic `aria-label` ("Add <name> to favorites" / "Remove <name> from
 *   favorites") so screen readers always have the visible context.
 * - Star glyph uses `currentColor` so it adapts to forced-colors mode and
 *   the active Bootstrap color scheme.
 */
import { useIsAuthenticated } from '@azure/msal-react';
import type { FavoriteKind } from '../types';
import { useFavorites } from '../lib/favorites';

interface Props {
  kind: FavoriteKind;
  targetId: string;
  /** Used in `aria-label` and tooltip — should be the user-visible name. */
  targetName: string;
  /** Tailwind/Bootstrap class overrides. Defaults to a small icon-only button. */
  className?: string;
  /** Stop click events from bubbling (use inside clickable cards). Default true. */
  stopPropagation?: boolean;
}

export default function FavoriteButton({
  kind,
  targetId,
  targetName,
  className,
  stopPropagation = true
}: Props) {
  const isAuthed = useIsAuthenticated();
  const { isFavorite, toggle, ready } = useFavorites();
  if (!isAuthed) return null;
  const fav = isFavorite(kind, targetId);
  const label = fav
    ? `Remove ${targetName} from favorites`
    : `Add ${targetName} to favorites`;
  const cls = className ?? 'btn btn-sm btn-link p-1 mh-favorite-btn';
  return (
    <button
      type="button"
      className={cls}
      aria-pressed={fav}
      aria-label={label}
      title={label}
      disabled={!ready}
      onClick={(e) => {
        if (stopPropagation) {
          e.stopPropagation();
          e.preventDefault();
        }
        void toggle(kind, targetId);
      }}
    >
      <span aria-hidden="true" className={fav ? 'mh-favorite-star is-on' : 'mh-favorite-star'}>
        <i className={fav ? 'bi bi-star-fill' : 'bi bi-star'} />
      </span>
    </button>
  );
}
